import { useEffect, useState } from 'react'
import { emptyOrderLocation } from '../../../../constants/carpoolLocation'
import OrderLocationCard from './OrderLocationCard'
import OrderLocationFields from './OrderLocationFields'
import useLocationValidation from '../../../../hooks/useLocationValidation'
import useGooglePlaces from '../../../../hooks/useGooglePlaces'

export default function OrderLocationStep({ stepRef, label, apiLocation, quoteLocation, locationType, value, onChange, accentColor, createdBy, verifiedRef, notesLabel }) {
  const { errors, validateWithAddress, markVerified, clearErrors, isLocationComplete } = useLocationValidation({ verifiedRef })
  const { validateAddress } = useGooglePlaces()

  const hasInitialValues = Object.values(value).some(Boolean)
  const isInitiallyComplete = hasInitialValues && isLocationComplete(value)

  const [isEditing, setIsEditing] = useState(!isInitiallyComplete)
  const [hasAutoSwitched, setHasAutoSwitched] = useState(isInitiallyComplete)
  const [snapshot, setSnapshot] = useState(null)

  const buildPrefilledLocation = (apiLocation, createdBy, locationType) => {
    const locationName = apiLocation.name || apiLocation['dealer-name'] || ''
    const isDealerPlace = apiLocation.type === 'DealerPlace'

    return {
      ...emptyOrderLocation,
      name: locationName,
      type: locationType,
      address: apiLocation.address1 || '',
      address_2: apiLocation.address2 || '',
      contact_name: (isDealerPlace ? createdBy : '') || locationName,
      contact_phone: apiLocation.phone || '',
      contact_email: apiLocation.email || '',
    }
  }

  useEffect(() => {
    if (!apiLocation) return
    onChange((prev) => {
      if (Object.values(prev).some(Boolean)) return prev
      const prefilled = buildPrefilledLocation(apiLocation, createdBy, locationType)
      markVerified(prefilled, quoteLocation)
      return prefilled
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiLocation, createdBy, locationType])

  useEffect(() => {
    if (hasAutoSwitched) return
    if (isLocationComplete(value)) {
      setIsEditing(false)
      setHasAutoSwitched(true)
    }
  }, [value, hasAutoSwitched])

  if (stepRef) {
    stepRef.current = {
      validate: () => validateWithAddress(value, quoteLocation, validateAddress),
      markVerified: (location) => markVerified(location || value, quoteLocation),
    }
  }

  const handleChange = (newValue) => {
    setHasAutoSwitched(true)
    clearErrors()
    onChange(newValue)
  }

  const handleClear = () => {
    onChange({ ...emptyOrderLocation })
    clearErrors()
  }

  const handleEditClick = () => {
    setSnapshot(value)
    setIsEditing(true)
  }

  const handleCancelEdit = () => {
    if (snapshot) onChange(snapshot)
    setSnapshot(null)
    setIsEditing(false)
    clearErrors()
  }

  if (!isEditing) {
    return (
      <OrderLocationCard
        label={label}
        values={value}
        quoteLocation={quoteLocation}
        locationType={locationType}
        accentColor={accentColor}
        onClick={handleEditClick}
        onChange={onChange}
        notesLabel={notesLabel}
      />
    )
  }

  return (
    <OrderLocationFields
      label={label}
      quoteLocation={quoteLocation}
      locationType={locationType}
      values={value}
      onChange={handleChange}
      onClear={handleClear}
      onCancel={snapshot ? handleCancelEdit : null}
      accentColor={accentColor}
      errors={errors}
      notesLabel={notesLabel}
    />
  )
}
